import graphql__a610133d41297b1743bd from "./__generated__/EditBwaMonthDialogMutation.graphql.ts";import * as React from "react";
import {graphql} from "react-relay";
import {useRelayEnvironment} from "react-relay/hooks";
import {useIntl} from "react-intl";
import * as yup from "yup";
import FormDialog from "./FormDialog";
import Section from "../form/Section";
import Row from "../form/Row";
import Label from "../form/Label";
import Field from "../form/Field";
import {commitMutationWithErrorHandling} from "../../helper/commitMutationWithErrorHandling";
import {formatCentsForInput, parseAmountToCents, yupAmount} from "../../helper/amountInput";
import {useErrorContext} from "../../contexts/ErrorContext";
import {bwaFigureNames, type BwaFigures} from "../../helper/bwaFigures";
import type {EditBwaMonthDialogMutation} from "./__generated__/EditBwaMonthDialogMutation.graphql";

type Props = {
    // Without a BWA month, the dialog records a new one
    bwaMonth?: {
        month: string
        figures: BwaFigures
        balances: ReadonlyArray<{
            bankAccountIdentifier: string
            closingBalance: number
        }>
    }
    bankAccounts: ReadonlyArray<{
        bankAccountIdentifier: string
        name: string
    }>
    // The month a new record starts with
    defaultMonth: string
    onClose: (changed: boolean) => void
}

const balanceFieldName = (bankAccountIdentifier: string) => 'balance-' + bankAccountIdentifier;

const recordMutation = graphql__a610133d41297b1743bd;

/**
 * Records the BWA values of a month or corrects them. The fields follow the order of the BWA, so
 * the values can be typed from top to bottom with the tab key.
 */
const EditBwaMonthDialog: React.FC<Props> = ({bwaMonth, bankAccounts, defaultMonth, onClose}) => {
    const intl = useIntl();
    const relayEnvironment = useRelayEnvironment();
    const {addError} = useErrorContext();

    const handleSubmission = (values) => {
        const balances = bankAccounts
            .filter(bankAccount => values[balanceFieldName(bankAccount.bankAccountIdentifier)].trim() !== '')
            .map(bankAccount => ({
                bankAccountIdentifier: bankAccount.bankAccountIdentifier,
                closingBalance: parseAmountToCents(values[balanceFieldName(bankAccount.bankAccountIdentifier)]) as number,
            }));
        commitMutationWithErrorHandling<EditBwaMonthDialogMutation>(
            relayEnvironment,
            {
                mutation: recordMutation,
                variables: {
                    input: {
                        month: values.month.trim(),
                        figures: Object.fromEntries(bwaFigureNames.map(field => [field, parseAmountToCents(values[field]) as number])) as BwaFigures,
                        balances,
                    },
                },
                onCompleted: (_, errors) => {
                    if (errors && errors.length > 0) {
                        console.error(errors);
                    }
                    onClose(true);
                },
                onError: (error) => {
                    console.error(error);
                    onClose(false);
                },
            },
            addError,
            {customErrorMessage: intl.formatMessage({id: 'errors.failedToRecordBwaMonth'})}
        );
    };

    const validationSchema = yup.object().shape({
        month: yup.string()
            .trim()
            .required(intl.formatMessage({id: 'validation.field.required'}))
            .matches(/^\d{4}-(0[1-9]|1[0-2])$/, intl.formatMessage({id: 'validation.field.invalidMonth'})),
        ...Object.fromEntries(bwaFigureNames.map(field => [field, yupAmount(intl)])),
        ...Object.fromEntries(bankAccounts.map(bankAccount => [balanceFieldName(bankAccount.bankAccountIdentifier), yupAmount(intl, {optional: true})])),
    });

    const initialValues = {
        month: bwaMonth?.month ?? defaultMonth,
        ...Object.fromEntries(bwaFigureNames.map(field => [field, bwaMonth ? formatCentsForInput(bwaMonth.figures[field]) : ''])),
        ...Object.fromEntries(bankAccounts.map(bankAccount => {
            const balance = bwaMonth?.balances.find(candidate => candidate.bankAccountIdentifier === bankAccount.bankAccountIdentifier);
            return [balanceFieldName(bankAccount.bankAccountIdentifier), balance ? formatCentsForInput(balance.closingBalance) : ''];
        })),
    };

    return (
        <FormDialog handleCloseDialog={() => onClose(false)}
                    handleSubmission={handleSubmission}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    headline={intl.formatMessage({id: bwaMonth ? 'dialog.EditBwaMonthDialog.editHeadline' : 'dialog.EditBwaMonthDialog.recordHeadline'})}
                    submitLabel={intl.formatMessage({id: 'button.save'})}>
            <Section>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.EditBwaMonthDialog.monthLabel'})}</Label>
                    <Field name="month" type="text" placeholder="2026-08" disabled={bwaMonth !== undefined} autoFocus={bwaMonth === undefined} data-1p-ignore="true"/>
                </Row>
                {bwaFigureNames.map((field, index) => (
                    <Row key={field}>
                        <Label>{intl.formatMessage({id: 'dialog.EditBwaMonthDialog.' + field + 'Label'})}</Label>
                        <Field name={field} type="text" inputMode="decimal" placeholder="0,00" autoFocus={bwaMonth !== undefined && index === 0} data-1p-ignore="true"/>
                        {field === 'otherCosts' && (
                            <p className="text-sm text-gray-500">{intl.formatMessage({id: 'dialog.EditBwaMonthDialog.otherCostsHint'})}</p>
                        )}
                    </Row>
                ))}
                {bankAccounts.map(bankAccount => (
                    <Row key={bankAccount.bankAccountIdentifier}>
                        <Label>{intl.formatMessage({id: 'dialog.EditBwaMonthDialog.closingBalanceLabel'}, {account: bankAccount.name})}</Label>
                        <Field name={balanceFieldName(bankAccount.bankAccountIdentifier)} type="text" inputMode="decimal" placeholder="0,00" data-1p-ignore="true"/>
                    </Row>
                ))}
                <p className="text-sm text-gray-500">
                    {intl.formatMessage({id: 'dialog.EditBwaMonthDialog.description'})}
                </p>
            </Section>
        </FormDialog>
    );
};

export default EditBwaMonthDialog;
